import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import api, { fileAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { cn, formatDate, getUploadErrorMessage } from '../lib/utils';
import {
    FolderOpen,
    Folder,
    FolderPlus,
    Search,
    File,
    Image,
    Video,
    Download,
    ExternalLink,
    Loader2,
    FileText,
    Grid,
    List,
    Upload,
    Trash2,
    Plus,
    Home,
    ChevronRight,
    Edit2,
    Eye,
} from 'lucide-react';
import FilePreviewModal, { isOfficeFile, isBrowserViewable } from '../components/FilePreviewModal';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function FileManagerPage() {
    const { spaceId } = useParams();
    const { spaces, currentWorkspace, fetchWorkspaces, selectWorkspace, workspaces, storageStats, refreshStorage } = useWorkspace();
    const [files, setFiles] = useState([]);
    const [folders, setFolders] = useState([]);
    const [currentFolderId, setCurrentFolderId] = useState(null); // null = root
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [viewMode, setViewMode] = useState('list');
    const [filterType, setFilterType] = useState('all');
    const [initializing, setInitializing] = useState(true);
    // New folder dialog
    const [newFolderOpen, setNewFolderOpen] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [creatingFolder, setCreatingFolder] = useState(false);
    // Rename folder dialog
    const [renameFolder, setRenameFolder] = useState(null);
    const [renameValue, setRenameValue] = useState('');
    // File preview
    const [previewFile, setPreviewFile] = useState(null);
    const fileInputRef = useRef(null);

    const space = spaces.find(s => s.id === spaceId);

    // Auto-select workspace if not selected
    useEffect(() => {
        const initWorkspace = async () => {
            if (!currentWorkspace) {
                const ws = await fetchWorkspaces();
                if (ws.length > 0) {
                    await selectWorkspace(ws[0]);
                }
            }
            setInitializing(false);
        };
        initWorkspace();
    }, [currentWorkspace, fetchWorkspaces, selectWorkspace]);

    useEffect(() => {
        if (spaceId && !initializing) {
            fetchFiles();
        }
    }, [spaceId, initializing]);

    const fetchFiles = async () => {
        setLoading(true);
        try {
            const [fRes, folderRes] = await Promise.all([
                fileAPI.getBySpace(spaceId),
                api.get(`/file-folders/space/${spaceId}`),
            ]);
            setFiles(fRes.data);
            setFolders(folderRes.data || []);
        } catch (error) {
            console.error('Failed to fetch files:', error);
            toast.error('Gagal memuat file');
        } finally {
            setLoading(false);
        }
    };

    // Folder helpers
    const currentFolders = folders.filter(f => (f.parent_folder_id || null) === currentFolderId);
    const unexistFolder = (fid) => fid && !folders.find(f => f.id === fid);
    const currentFiles = files.filter(f => currentFolderId === null ? ((f.folder_id || null) === currentFolderId || unexistFolder(f.folder_id)) : (f.folder_id || null) === currentFolderId);

    const buildBreadcrumb = () => {
        if (!currentFolderId) return [];
        const byId = new Map(folders.map(f => [f.id, f]));
        const chain = [];
        let node = byId.get(currentFolderId);
        while (node) {
            chain.unshift(node);
            node = node.parent_folder_id ? byId.get(node.parent_folder_id) : null;
        }
        return chain;
    };
    const breadcrumb = buildBreadcrumb();

    const handleCreateFolder = async () => {
        const name = newFolderName.trim();
        if (!name) return toast.error('Nama folder wajib diisi');
        try {
            setCreatingFolder(true);
            await api.post('/file-folders', {
                space_id: spaceId,
                name,
                parent_folder_id: currentFolderId,
            });
            toast.success('Folder dibuat');
            setNewFolderOpen(false);
            setNewFolderName('');
            await fetchFiles();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Gagal membuat folder');
        } finally {
            setCreatingFolder(false);
        }
    };

    const handleDeleteFolder = async (folder) => {
        if (!window.confirm(`Hapus folder "${folder.name}" beserta semua isinya? Tindakan ini tidak bisa dibatalkan.`)) return;
        try {
            const res = await api.delete(`/file-folders/${folder.id}`);
            toast.success(`Folder dihapus (${res.data.deleted_files} file)`);
            await fetchFiles();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Gagal menghapus folder');
        }
    };

    const handleRenameFolder = async () => {
        const name = renameValue.trim();
        if (!name) return toast.error('Nama tidak boleh kosong');
        try {
            await api.patch(`/file-folders/${renameFolder.id}/rename`, { name });
            toast.success('Folder diubah');
            setRenameFolder(null);
            setRenameValue('');
            await fetchFiles();
        } catch (e) {
            toast.error('Gagal mengubah nama');
        }
    };

    // ---- Drag & drop ------------------------------------------------------
    const [draggedFileId, setDraggedFileId] = useState(null);
    const [dropTargetFolderId, setDropTargetFolderId] = useState(null); // undefined | 'root' | folderId

    const handleFileDragStart = (fileId) => (e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', fileId);
        setDraggedFileId(fileId);
    };
    const handleFileDragEnd = () => {
        setDraggedFileId(null);
        setDropTargetFolderId(null);
    };

    const handleDropOnFolder = (targetFolderId) => async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const fileId = e.dataTransfer.getData('text/plain') || draggedFileId;
        setDropTargetFolderId(null);
        setDraggedFileId(null);
        if (!fileId) return;
        // No-op if dropping into the same folder
        const f = files.find((x) => x.id === fileId);
        if (f && (f.folder_id || null) === (targetFolderId || null)) return;
        try {
            await fileAPI.moveToFolder(fileId, targetFolderId);
            toast.success(targetFolderId ? 'File dipindahkan ke folder' : 'File dipindahkan ke root');
            await fetchFiles();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Gagal memindahkan file');
        }
    };

    const handleDragOverFolder = (targetFolderId) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
        if (dropTargetFolderId !== targetFolderId) setDropTargetFolderId(targetFolderId);
    };
    const handleDragLeaveFolder = () => setDropTargetFolderId(null);

    const handleUpload = async (e) => {
        const selectedFiles = e.target.files;
        if (!selectedFiles || selectedFiles.length === 0) return;

        setUploading(true);
        let successCount = 0;

        for (const file of selectedFiles) {
            try {
                await fileAPI.upload(file, null, spaceId, currentFolderId);
                successCount++;
            } catch (error) {
                console.error('Failed to upload file:', file.name, error);
                toast.error(getUploadErrorMessage(error));
            }
        }

        if (successCount > 0) {
            toast.success(`${successCount} file berhasil diupload`);
            fetchFiles();
            refreshStorage();
        }
        setUploading(false);
        
        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleDelete = async (fileId, filename) => {
        if (!window.confirm(`Hapus file "${filename}"?`)) return;
        
        try {
            await fileAPI.delete(fileId);
            toast.success('File berhasil dihapus');
            fetchFiles();
        } catch (error) {
            console.error('Failed to delete file:', error);
            toast.error('Gagal menghapus file');
        }
    };

    const getFileIcon = (file) => {
        const type = file.type || 'file';
        if (type === 'image') return <Image className="w-8 h-8 text-brand-info" />;
        if (type === 'video') return <Video className="w-8 h-8 text-brand-error" />;
        return <FileText className="w-8 h-8 text-brand-primary" />;
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '-';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const filteredFiles = currentFiles.filter(file => {
        const query = searchQuery.toLowerCase();
        const matchesSearch = file.filename?.toLowerCase().includes(query) ||
            file.task_title?.toLowerCase().includes(query);
        const matchesType = filterType === 'all' || file.type === filterType;
        return matchesSearch && matchesType;
    });

    const filteredFolders = currentFolders.filter(f =>
        !searchQuery || f.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Group files by type for stats
    const imageCount = files.filter(f => f.type === 'image').length;
    const videoCount = files.filter(f => f.type === 'video').length;
    const fileCount = files.filter(f => f.type === 'file').length;

    if (initializing || !currentWorkspace) {
        return (
            <Layout title="File Manager">
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout 
            title="File Manager" 
            subtitle={space ? `File di space "${space.name}"` : 'Kelola file space'}
        >
            <div className="space-y-6" data-testid="file-manager-page">
                {/* Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                    <Card className="border-brand-border p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-brand-accent-soft rounded-lg flex items-center justify-center">
                                <FolderOpen className="w-5 h-5 text-brand-primary" />
                            </div>
                            <div>
                                <p className="text-sm text-brand-text-secondary">Total File</p>
                                <p className="text-2xl font-bold text-brand-text-primary">{files.length}</p>
                            </div>
                        </div>
                    </Card>
                    <Card 
                        className={cn(
                            "border-brand-border p-4 cursor-pointer transition-colors",
                            filterType === 'image' && "border-brand-info bg-brand-info/5"
                        )}
                        onClick={() => setFilterType(filterType === 'image' ? 'all' : 'image')}
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-brand-info/10 rounded-lg flex items-center justify-center">
                                <Image className="w-5 h-5 text-brand-info" />
                            </div>
                            <div>
                                <p className="text-sm text-brand-text-secondary">Gambar</p>
                                <p className="text-2xl font-bold text-brand-info">{imageCount}</p>
                            </div>
                        </div>
                    </Card>
                    <Card 
                        className={cn(
                            "border-brand-border p-4 cursor-pointer transition-colors",
                            filterType === 'video' && "border-brand-error bg-brand-error/5"
                        )}
                        onClick={() => setFilterType(filterType === 'video' ? 'all' : 'video')}
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-brand-error/10 rounded-lg flex items-center justify-center">
                                <Video className="w-5 h-5 text-brand-error" />
                            </div>
                            <div>
                                <p className="text-sm text-brand-text-secondary">Video</p>
                                <p className="text-2xl font-bold text-brand-error">{videoCount}</p>
                            </div>
                        </div>
                    </Card>
                    <Card
                        className={cn(
                            "border-brand-border p-4 cursor-pointer transition-colors",
                            filterType === 'file' && "border-brand-primary bg-brand-primary/5"
                        )}
                        onClick={() => setFilterType(filterType === 'file' ? 'all' : 'file')}
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-brand-accent-soft rounded-lg flex items-center justify-center">
                                <File className="w-5 h-5 text-brand-primary" />
                            </div>
                            <div>
                                <p className="text-sm text-brand-text-secondary">Dokumen</p>
                                <p className="text-2xl font-bold text-brand-primary">{fileCount}</p>
                            </div>
                        </div>
                    </Card>
                    {storageStats && (
                        <Card className="border-brand-border p-4">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 bg-brand-accent-soft rounded-lg flex items-center justify-center">
                                    <FolderOpen className="w-5 h-5 text-brand-primary" />
                                </div>
                                <div>
                                    <p className="text-sm text-brand-text-secondary">Storage</p>
                                    <p className="text-lg font-bold text-brand-text-primary">
                                        {storageStats.used_gb.toFixed(1)} <span className="text-sm font-normal text-brand-text-secondary">/ {storageStats.quota_gb} GB</span>
                                    </p>
                                </div>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                                <div
                                    className={`h-1.5 rounded-full transition-all ${
                                        storageStats.percent >= 95 ? 'bg-red-500' :
                                        storageStats.percent >= 80 ? 'bg-yellow-400' : 'bg-brand-primary'
                                    }`}
                                    style={{ width: `${Math.min(storageStats.percent, 100)}%` }}
                                />
                            </div>
                        </Card>
                    )}
                </div>

                {/* Breadcrumb */}
                <div className="flex items-center gap-1 text-sm" data-testid="file-breadcrumb">
                    <button
                        onClick={() => setCurrentFolderId(null)}
                        onDragOver={handleDragOverFolder(null)}
                        onDragLeave={handleDragLeaveFolder}
                        onDrop={handleDropOnFolder(null)}
                        className={cn(
                            'inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-brand-accent-soft transition-colors',
                            !currentFolderId && 'text-brand-primary font-medium',
                            dropTargetFolderId === null && draggedFileId && 'ring-2 ring-brand-primary bg-brand-accent-soft',
                        )}
                        data-testid="breadcrumb-root"
                    >
                        <Home className="w-4 h-4" /> Root
                    </button>
                    {breadcrumb.map((f) => (
                        <React.Fragment key={f.id}>
                            <ChevronRight className="w-4 h-4 text-brand-text-muted" />
                            <button
                                onClick={() => setCurrentFolderId(f.id)}
                                className={cn(
                                    'px-2 py-1 rounded hover:bg-brand-accent-soft',
                                    currentFolderId === f.id && 'text-brand-primary font-medium'
                                )}
                            >
                                {f.name}
                            </button>
                        </React.Fragment>
                    ))}
                </div>

                {/* Header Actions */}
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="relative w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-text-muted" />
                        <Input
                            placeholder="Cari file..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9"
                            data-testid="search-file-input"
                        />
                    </div>
                    <div className="flex items-center gap-3">
                        {/* New Folder */}
                        <Button
                            variant="outline"
                            onClick={() => setNewFolderOpen(true)}
                            data-testid="new-folder-btn"
                        >
                            <FolderPlus className="w-4 h-4 mr-2" /> New Folder
                        </Button>
                        {/* Upload Button */}
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleUpload}
                            multiple
                            className="hidden"
                            data-testid="file-upload-input"
                        />
                        <Button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            className="bg-brand-primary hover:bg-brand-primary-dark"
                            data-testid="upload-file-btn"
                        >
                            {uploading ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Upload className="w-4 h-4 mr-2" />
                            )}
                            Upload File
                        </Button>
                        
                        {/* View Toggle */}
                        <div className="flex items-center bg-brand-accent-soft rounded-lg p-1">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={cn(
                                    'p-1.5 rounded-md transition-colors',
                                    viewMode === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-white/50'
                                )}
                                data-testid="view-grid-btn"
                            >
                                <Grid className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={cn(
                                    'p-1.5 rounded-md transition-colors',
                                    viewMode === 'list' ? 'bg-white shadow-sm' : 'hover:bg-white/50'
                                )}
                                data-testid="view-list-btn"
                            >
                                <List className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Folders — always grid thumbnail regardless of viewMode */}
                {!loading && filteredFolders.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        {filteredFolders.map((f) => (
                            <Card
                                key={f.id}
                                className={cn(
                                    'border-brand-border p-4 hover:shadow-md transition-all cursor-pointer group',
                                    dropTargetFolderId === f.id && 'ring-2 ring-brand-primary scale-[1.02]',
                                )}
                                onClick={() => setCurrentFolderId(f.id)}
                                onDragOver={handleDragOverFolder(f.id)}
                                onDragLeave={handleDragLeaveFolder}
                                onDrop={handleDropOnFolder(f.id)}
                                data-testid={`folder-card-${f.id}`}
                            >
                                <div className="flex flex-col items-center text-center">
                                    <div className="w-full h-24 mb-3 rounded-lg bg-brand-accent-soft flex items-center justify-center">
                                        <Folder className="w-12 h-12 text-brand-primary" strokeWidth={1.5} />
                                    </div>
                                    <p className="text-sm font-medium truncate w-full" title={f.name}>{f.name}</p>
                                    <div className="flex items-center gap-2 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setRenameFolder(f); setRenameValue(f.name); }}
                                            className="p-1.5 rounded-md bg-brand-accent-soft hover:bg-brand-primary hover:text-white"
                                            title="Ubah nama"
                                            data-testid={`rename-folder-${f.id}`}
                                        >
                                            <Edit2 className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleDeleteFolder(f); }}
                                            className="p-1.5 rounded-md bg-brand-accent-soft hover:bg-red-600 hover:text-white"
                                            title="Hapus folder"
                                            data-testid={`delete-folder-${f.id}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                )}

                {/* Files Display */}
                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                    </div>
                ) : filteredFiles.length > 0 ? (
                    viewMode === 'grid' ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                            {filteredFiles.map(file => (
                                <Card 
                                    key={file.id} 
                                    className={cn(
                                        'border-brand-border p-4 hover:shadow-md transition-shadow group cursor-pointer',
                                        draggedFileId === file.id && 'opacity-50',
                                    )}
                                    draggable
                                    onDragStart={handleFileDragStart(file.id)}
                                    onDragEnd={handleFileDragEnd}
                                    onClick={() => setPreviewFile(file)}
                                    data-testid={`file-card-${file.id}`}
                                >
                                    <div className="flex flex-col items-center text-center">
                                        {file.type === 'image' ? (
                                            <div className="w-full h-24 mb-3 rounded-lg overflow-hidden bg-brand-accent-soft">
                                                <img 
                                                    src={`${BACKEND_URL}${file.url}`} 
                                                    alt={file.filename}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                        ) : (
                                            <div className="w-full h-24 mb-3 rounded-lg bg-brand-accent-soft flex items-center justify-center">
                                                {getFileIcon(file)}
                                            </div>
                                        )}
                                        <p className="text-sm font-medium text-brand-text-primary truncate w-full" title={file.filename}>
                                            {file.filename}
                                        </p>
                                        <p className="text-xs text-brand-text-muted mt-1">
                                            {formatFileSize(file.size)}
                                        </p>
                                        {file.task_code && (
                                            <Link 
                                                to={`/tasks?task=${file.task_id}`}
                                                className="text-xs text-brand-primary hover:underline mt-1"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {file.task_code}
                                            </Link>
                                        )}
                                        <div className="flex items-center gap-2 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                                            {isBrowserViewable(file.filename) && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); window.open(`${BACKEND_URL}${file.url}`, '_blank', 'noopener,noreferrer'); }}
                                                    className="p-1.5 rounded-md bg-brand-accent-soft hover:bg-brand-primary hover:text-white transition-colors"
                                                    title="Buka di tab baru"
                                                >
                                                    <ExternalLink className="w-4 h-4" />
                                                </button>
                                            )}
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setPreviewFile(file); }}
                                                className="p-1.5 rounded-md bg-brand-accent-soft hover:bg-brand-primary hover:text-white transition-colors"
                                                title="Preview"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </button>
                                            <a
                                                href={`${BACKEND_URL}${file.url}`}
                                                download={file.filename}
                                                onClick={(e) => e.stopPropagation()}
                                                className="p-1.5 rounded-md bg-brand-accent-soft hover:bg-brand-primary hover:text-white transition-colors"
                                                title="Download"
                                            >
                                                <Download className="w-4 h-4" />
                                            </a>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleDelete(file.id, file.filename); }}
                                                className="p-1.5 rounded-md bg-brand-accent-soft hover:bg-brand-error hover:text-white transition-colors"
                                                title="Hapus"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    ) : (
                        <Card className="border-brand-border divide-y divide-brand-border">
                            {filteredFiles.map(file => (
                                <div 
                                    key={file.id}
                                    className={cn(
                                        'flex items-center gap-4 p-4 hover:bg-brand-accent-soft/30 transition-colors cursor-pointer',
                                        draggedFileId === file.id && 'opacity-50',
                                    )}
                                    draggable
                                    onDragStart={handleFileDragStart(file.id)}
                                    onDragEnd={handleFileDragEnd}
                                    onClick={() => setPreviewFile(file)}
                                    data-testid={`file-row-${file.id}`}
                                >
                                    <div className="w-10 h-10 rounded-lg bg-brand-accent-soft flex items-center justify-center flex-shrink-0">
                                        {file.type === 'image' ? (
                                            <Image className="w-5 h-5 text-brand-info" />
                                        ) : file.type === 'video' ? (
                                            <Video className="w-5 h-5 text-brand-error" />
                                        ) : (
                                            <FileText className="w-5 h-5 text-brand-primary" />
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-brand-text-primary truncate">
                                            {file.filename}
                                        </p>
                                        <div className="flex items-center gap-3 text-xs text-brand-text-muted mt-0.5">
                                            <span>{formatFileSize(file.size)}</span>
                                            {file.task_code && (
                                                <Link 
                                                    to={`/tasks?task=${file.task_id}`}
                                                    className="text-brand-primary hover:underline"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {file.task_code}: {file.task_title}
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {isBrowserViewable(file.filename) && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); window.open(`${BACKEND_URL}${file.url}`, '_blank', 'noopener,noreferrer'); }}
                                                className="p-2 rounded-md hover:bg-brand-accent-soft transition-colors"
                                                title="Buka di tab baru"
                                            >
                                                <ExternalLink className="w-4 h-4 text-brand-text-muted" />
                                            </button>
                                        )}
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setPreviewFile(file); }}
                                            className="p-2 rounded-md hover:bg-brand-accent-soft transition-colors"
                                            title="Preview"
                                        >
                                            <Eye className="w-4 h-4 text-brand-text-muted" />
                                        </button>
                                        <a
                                            href={`${BACKEND_URL}${file.url}`}
                                            download={file.filename}
                                            onClick={(e) => e.stopPropagation()}
                                            className="p-2 rounded-md hover:bg-brand-accent-soft transition-colors"
                                            title="Download"
                                        >
                                            <Download className="w-4 h-4 text-brand-text-muted" />
                                        </a>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleDelete(file.id, file.filename); }}
                                            className="p-2 rounded-md hover:bg-brand-error/10 transition-colors"
                                            title="Hapus"
                                        >
                                            <Trash2 className="w-4 h-4 text-brand-text-muted hover:text-brand-error" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </Card>
                    )
                ) : (
                    <div className="empty-state">
                        <FolderOpen className="empty-state-icon" strokeWidth={1.5} />
                        <h2 className="empty-state-title">Belum ada file</h2>
                        <p className="empty-state-description">
                            File yang diupload ke task di space ini akan muncul di sini.
                        </p>
                    </div>
                )}
            </div>

            {/* New Folder Dialog */}
            <Dialog open={newFolderOpen} onOpenChange={setNewFolderOpen}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Buat Folder Baru</DialogTitle>
                    </DialogHeader>
                    <div>
                        <Label>Nama Folder</Label>
                        <Input
                            autoFocus
                            value={newFolderName}
                            onChange={(e) => setNewFolderName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleCreateFolder(); }}
                            placeholder="Misal: Dokumen Kontrak"
                            data-testid="new-folder-name-input"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setNewFolderOpen(false)}>Batal</Button>
                        <Button onClick={handleCreateFolder} disabled={creatingFolder} data-testid="new-folder-save-btn">
                            {creatingFolder && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Buat
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Rename Folder Dialog */}
            <Dialog open={!!renameFolder} onOpenChange={(o) => !o && setRenameFolder(null)}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Ubah Nama Folder</DialogTitle>
                    </DialogHeader>
                    <div>
                        <Label>Nama Folder</Label>
                        <Input
                            autoFocus
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleRenameFolder(); }}
                            data-testid="rename-folder-input"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRenameFolder(null)}>Batal</Button>
                        <Button onClick={handleRenameFolder} data-testid="rename-folder-save-btn">Simpan</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* File Preview Modal */}
            <FilePreviewModal
                file={previewFile}
                open={!!previewFile}
                onClose={() => setPreviewFile(null)}
            />
        </Layout>
    );
}
